import type {BusinessFeeNegotiation} from '@afriex/core/domain/types';
import {getNegotiationDisplay} from '@afriex/core/domain/types';

import {
  Button,
  MinusIcon,
  PlusIcon,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';

import {NegotiationStatusBadge} from './NegotiationBadges';
import {
  getClosedOn,
  getCorridorCount,
  getLastActivity,
} from './negotiationFormat';
import NegotiationReview from './NegotiationReview';

type NegotiationsTableProps = {
  negotiations: BusinessFeeNegotiation[];
  /** The Completed tab swaps in Rounds, Outcome and Closed. */
  isCompleted: boolean;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onAnswered: () => void;
};

const NegotiationRow = ({
  negotiation,
  index,
  isCompleted,
  isExpanded,
  onToggle,
  onAnswered,
}: {
  negotiation: BusinessFeeNegotiation;
  index: number;
  isCompleted: boolean;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  onAnswered: () => void;
}) => {
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  const name = negotiation.businessName ?? negotiation.businessId;
  const roundCount = negotiation.rounds?.length ?? 0;

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric className="px-4">
          <Button
            onClick={() => negotiation.id && onToggle(negotiation.id)}
            variant="unStyled"
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${name}`}
            aria-expanded={isExpanded}
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 dark:hover:bg-gray-700 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>
        <Td className="px-4">
          <span className="block font-bold">{name}</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            {negotiation.businessReference ?? negotiation.businessId}
          </span>
        </Td>
        <Td className="px-4">{getCorridorCount(negotiation)}</Td>
        <Td className="px-4">
          {isCompleted
            ? roundCount
            : getNegotiationDisplay(negotiation).roundLabel}
        </Td>
        <Td className="px-4">
          <NegotiationStatusBadge negotiation={negotiation} />
        </Td>
        <Td className="px-4">
          {isCompleted
            ? getClosedOn(negotiation)
            : getLastActivity(negotiation)}
        </Td>
      </Tr>

      {isExpanded ? (
        <Tr enableHover={false}>
          <Td colspan={6} className="p-0 m-0 whitespace-normal">
            <div className="flex min-w-0 py-5 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <NegotiationReview
                key={negotiation.id}
                negotiation={negotiation}
                onAnswered={onAnswered}
              />
            </div>
          </Td>
        </Tr>
      ) : null}
    </>
  );
};

/** The queue as a table; opening a row shows its review underneath. */
const NegotiationsTable = ({
  negotiations,
  isCompleted,
  expandedId,
  onToggle,
  onAnswered,
}: NegotiationsTableProps) => (
  <Table className="overflow-hidden">
    <Thead>
      <Tr enableHover={false}>
        <Th className="w-12 px-4">&nbsp;</Th>
        <Th className="px-4">Merchant</Th>
        <Th className="px-4">Corridors</Th>
        <Th className="px-4">{isCompleted ? 'Rounds' : 'Round'}</Th>
        <Th className="px-4">{isCompleted ? 'Outcome' : 'Status'}</Th>
        <Th className="px-4">{isCompleted ? 'Closed' : 'Last activity'}</Th>
      </Tr>
    </Thead>
    <TBody>
      {negotiations.map((negotiation, index) => (
        <NegotiationRow
          key={negotiation.id ?? index}
          negotiation={negotiation}
          index={index}
          isCompleted={isCompleted}
          isExpanded={Boolean(negotiation.id) && negotiation.id === expandedId}
          onToggle={onToggle}
          onAnswered={onAnswered}
        />
      ))}
    </TBody>
  </Table>
);

export default NegotiationsTable;
